/**
 * one-bottle-limit.js
 * Restricts the cart to a single bottle (1 unit, any variant).
 *
 * Install: assets/one-bottle-limit.js
 * Include in <head> of layout/theme.liquid:
 *   <script src="{{ 'one-bottle-limit.js' | asset_url }}" defer="defer"></script>
 *
 * Use defer, not async. Deferred scripts execute in document order
 * before DOMContentLoaded, so this wrapper is installed before any
 * cart interaction is possible. async is unordered and could run
 * after a theme script has already taken its own reference to fetch.
 */
(function () {
  'use strict';

  var LIMIT = 1;

  var MESSAGES = {
    ko: '주문당 1병만 구매하실 수 있습니다. 다른 와인을 담으시려면 장바구니의 상품을 먼저 삭제해 주세요.',
    en: 'Only one bottle can be purchased per order. Remove the wine in your cart to choose a different one.'
  };

  var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  var MESSAGE = MESSAGES[lang] || MESSAGES.en;

  var nativeFetch = window.fetch.bind(window);

  /* ----------------------------------------------------------------
   * Message display
   * Uses #cart-errors on the cart page, otherwise a floating notice.
   * ---------------------------------------------------------------- */
  var noticeTimer = null;

  function showMessage() {
    var target = document.getElementById('cart-errors');

    if (target) {
      target.innerHTML =
        '<p class="error-message message" role="alert">' + MESSAGE + '</p>';
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var notice = document.querySelector('.bottle-limit-notice');

    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'bottle-limit-notice';
      notice.setAttribute('role', 'alert');
      notice.setAttribute('aria-live', 'assertive');
      document.body.appendChild(notice);
    }

    notice.textContent = MESSAGE;
    notice.classList.add('is-visible');

    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(function () {
      notice.classList.remove('is-visible');
    }, 5000);
  }

  /* ----------------------------------------------------------------
   * Quantity parsing
   * Cart AJAX bodies arrive as FormData, URLSearchParams or JSON.
   * ---------------------------------------------------------------- */
  function parseBody(body) {
    if (!body) return null;

    try {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        var out = {};
        body.forEach(function (value, key) { out[key] = value; });
        return out;
      }

      if (typeof body === 'string') {
        if (body.trim().charAt(0) === '{') return JSON.parse(body);

        var params = new URLSearchParams(body);
        var parsed = {};
        params.forEach(function (value, key) { parsed[key] = value; });
        return parsed;
      }

      if (typeof body === 'object') return body;
    } catch (e) {
      return null;
    }

    return null;
  }

  function addQuantity(body) {
    var data = parseBody(body);
    if (!data) return 1;

    // /cart/add.js also accepts an items array
    if (Array.isArray(data.items)) {
      return data.items.reduce(function (sum, item) {
        return sum + (Number(item.quantity) || 1);
      }, 0);
    }

    return Number(data.quantity) || 1;
  }

  function requestedQuantity(body) {
    var data = parseBody(body);
    if (!data) return null;

    // /cart/update.js sends { updates: { variantId: qty } }
    if (data.updates) {
      var values = Object.keys(data.updates).map(function (key) {
        return Number(data.updates[key]) || 0;
      });
      return values.length ? Math.max.apply(null, values) : null;
    }

    // /cart/change.js sends { id | line, quantity }
    if (data.quantity !== undefined) return Number(data.quantity) || 0;

    return null;
  }

  function currentItemCount() {
    return nativeFetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) { return cart.item_count; })
      .catch(function () { return 0; });
  }

  function blockedResponse() {
    showMessage();

    // Mirrors Shopify's own error shape so themes render it natively.
    return new Response(
      JSON.stringify({
        status: 422,
        message: MESSAGE,
        description: MESSAGE
      }),
      {
        status: 422,
        statusText: 'Unprocessable Entity',
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  /* ----------------------------------------------------------------
   * Intercept the Cart AJAX API
   * ---------------------------------------------------------------- */
  window.fetch = function (input, init) {
    var url = typeof input === 'string'
      ? input
      : (input && input.url) || '';

    var options = init || {};
    var body = options.body;

    if (/\/cart\/add/.test(url)) {
      var adding = addQuantity(body);

      if (adding > LIMIT) return Promise.resolve(blockedResponse());

      return currentItemCount().then(function (count) {
        if (count + adding > LIMIT) return blockedResponse();
        return nativeFetch(input, init);
      });
    }

    if (/\/cart\/(change|update)/.test(url)) {
      var requested = requestedQuantity(body);

      if (requested !== null && requested > LIMIT) {
        return Promise.resolve(blockedResponse());
      }
    }

    return nativeFetch(input, init);
  };

  /* ----------------------------------------------------------------
   * Intercept non-AJAX add-to-cart form posts
   *
   * Only /cart/add forms are checked here. The cart page's own form
   * (action="/cart") must NOT be intercepted: cancelling and calling
   * form.submit() drops the submit button's name/value, so the POST
   * arrives without `checkout` and Shopify treats it as a plain cart
   * update — 302 straight back to /cart. The check would be redundant
   * there anyway, since the cart was already capped on the way in.
   * ---------------------------------------------------------------- */
  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.bottleLimitChecked === 'true') return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    var data = new FormData(form);
    var quantity = Number(data.get('quantity')) || 1;

    event.preventDefault();

    if (quantity > LIMIT) {
      showMessage();
      return;
    }

    currentItemCount().then(function (count) {
      if (count + quantity > LIMIT) {
        showMessage();
        return;
      }

      form.dataset.bottleLimitChecked = 'true';
      form.submit();
    });
  }, true);

  /* ----------------------------------------------------------------
   * Cart page: cap the quantity controls
   * ---------------------------------------------------------------- */
  function capQuantityControls() {
    var inputs = document.querySelectorAll('#main-cart .quantity-input');

    Array.prototype.forEach.call(inputs, function (input) {
      input.max = String(LIMIT);

      if (Number(input.value) > LIMIT) input.value = String(LIMIT);

      var plus = input.parentElement
        ? input.parentElement.querySelector('[name="plus"]')
        : null;

      if (plus) {
        plus.disabled = Number(input.value) >= LIMIT;
        plus.setAttribute('aria-disabled', plus.disabled ? 'true' : 'false');
      }
    });
  }

  function watchCart() {
    var cart = document.getElementById('main-cart');
    if (!cart) return;

    capQuantityControls();

    // The cart section re-renders after every quantity change.
    new MutationObserver(capQuantityControls).observe(cart, {
      childList: true,
      subtree: true
    });
  }

  // Typing straight into the quantity field
  document.addEventListener('change', function (event) {
    var input = event.target;

    if (!input.classList || !input.classList.contains('quantity-input')) return;

    if (Number(input.value) > LIMIT) {
      input.value = String(LIMIT);
      showMessage();
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCart);
  } else {
    watchCart();
  }
})();